import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import ProjectCard from './components/ProjectCard';
import DashboardToolbar from './components/DashboardToolbar';
import EmptyState from './components/EmptyState';
import ShareModal from './components/ShareModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import { Checkbox } from '../../components/ui/Checkbox';
import { useAuth } from '../../contexts/AuthContext';
import projectService from '../../services/projectService';
import VersionHistoryTimeline from './components/VersionHistoryTimeline';

const ProjectDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [filterTech, setFilterTech] = useState('all');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [shareModalProject, setShareModalProject] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [versionHistoryProject, setVersionHistoryProject] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/authentication');
      return;
    }
    loadProjects();
  }, [user, navigate]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService?.getAll(user?.id);
      setProjects(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedProjects = useMemo(() => {
    let filtered = [...projects];

    if (searchQuery) {
      filtered = filtered?.filter((project) =>
      project?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      project?.prompt?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (filterTech !== 'all') {
      filtered = filtered?.filter((project) =>
      project?.techStack?.some((tech) =>
      tech?.toLowerCase()?.includes(filterTech?.toLowerCase())
      )
      );
    }

    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name-asc':
          return a?.name?.localeCompare(b?.name);
        case 'name-desc':
          return b?.name?.localeCompare(a?.name);
        case 'size-desc':
          return parseFloat(b?.projectSize) - parseFloat(a?.projectSize);
        case 'size-asc':
          return parseFloat(a?.projectSize) - parseFloat(b?.projectSize);
        default:
          return 0;
      }
    });

    return filtered;
  }, [projects, searchQuery, sortBy, filterTech]);

  const handleNewProject = () => {
    navigate('/prompt-input');
  };

  const handleSelectProject = (projectId) => {
    setSelectedProjects((prev) =>
    prev?.includes(projectId) ?
    prev?.filter((id) => id !== projectId) :
    [...prev, projectId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProjects(filteredAndSortedProjects?.map((p) => p?.id));
    } else {
      setSelectedProjects([]);
    }
  };

  const handleDeleteProject = (projectId) => {
    const project = projects?.find((p) => p?.id === projectId);
    setDeleteConfirmation({
      projectId,
      projectName: project?.name,
      isMultiple: false
    });
  };

  const handleBulkDelete = () => {
    setDeleteConfirmation({
      projectIds: selectedProjects,
      isMultiple: true,
      count: selectedProjects?.length
    });
  };

  const confirmDelete = async () => {
    try {
      if (deleteConfirmation?.isMultiple) {
        await projectService?.bulkDelete(deleteConfirmation?.projectIds);
        setSelectedProjects([]);
      } else {
        await projectService?.delete(deleteConfirmation?.projectId);
      }
      await loadProjects();
    } catch (err) {
      setError(err?.message);
    } finally {
      setDeleteConfirmation(null);
    }
  };

  const handleBulkExport = () => {
    selectedProjects?.forEach((projectId) => {
      const project = projects?.find((p) => p?.id === projectId);
      if (project) {
        const link = document.createElement('a');
        link.href = project?.zipUrl;
        link.download = `${project?.name}.zip`;
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
      }
    });
  };

  const handleShareProject = (project) => {
    setShareModalProject(project);
  };

  const handleViewVersionHistory = (project) => {
    setVersionHistoryProject(project);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterTech('all');
    setSortBy('date-desc');
  };

  const hasActiveFilters = searchQuery || filterTech !== 'all';

  if (loading) {
    return (
      <>
        <Helmet>
          <title>My Projects - Giati Builder</title>
          <meta name="description" content="Manage and access your AI-generated software projects. View, download, and share your complete application codebases." />
        </Helmet>
        <div className="min-h-screen bg-background">
          <HeaderNavigation />
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading projects...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Projects - Giati Builder</title>
        <meta name="description" content="Manage and access your AI-generated software projects. View, download, and share your complete application codebases." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <HeaderNavigation />
        
        <main className="content-wrapper">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <DashboardToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy}
              filterTech={filterTech}
              onFilterTechChange={setFilterTech}
              onNewProject={handleNewProject}
              selectedCount={selectedProjects?.length}
              onBulkDelete={handleBulkDelete}
              onBulkExport={handleBulkExport} />


            {filteredAndSortedProjects?.length > 0 ?
            <>
                {filteredAndSortedProjects?.length > 1 &&
              <div className="mb-4 flex items-center gap-2">
                    <Checkbox
                  checked={selectedProjects?.length === filteredAndSortedProjects?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  label={`Select all (${filteredAndSortedProjects?.length} projects)`} />

                  </div>
              }

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                  {filteredAndSortedProjects?.map((project) =>
                <div key={project?.id} className="relative">
                      {filteredAndSortedProjects?.length > 1 &&
                  <div className="absolute top-3 left-3 z-10">
                          <Checkbox
                      checked={selectedProjects?.includes(project?.id)}
                      onChange={() => handleSelectProject(project?.id)}
                      className="bg-background/80 backdrop-blur-sm rounded p-1" />

                        </div>
                  }
                      <ProjectCard
                    project={project}
                    onDelete={handleDeleteProject}
                    onShare={handleShareProject}
                    onViewVersionHistory={handleViewVersionHistory} />

                    </div>
                )}
                </div>
              </> :

            <EmptyState
              hasFilters={hasActiveFilters}
              onClearFilters={handleClearFilters} />

            }

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}
          </div>
        </main>
      </div>
      {shareModalProject &&
      <ShareModal
        project={shareModalProject}
        onClose={() => setShareModalProject(null)} />

      }
      {deleteConfirmation &&
      <DeleteConfirmModal
        projectName={deleteConfirmation?.projectName}
        isMultiple={deleteConfirmation?.isMultiple}
        count={deleteConfirmation?.count}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirmation(null)} />

      }

      {versionHistoryProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Version History: {versionHistoryProject?.name}
              </h2>
              <button
                onClick={() => setVersionHistoryProject(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <VersionHistoryTimeline
                projectId={versionHistoryProject?.id}
                onRestore={() => {
                  loadProjects();
                  setVersionHistoryProject(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>);

};

export default ProjectDashboard;